'use client'

import { getSession } from 'next-auth/react'
import { API_URL, apiErrorResponse } from '../constants'
import { ApiResponse } from '../types'
import apiResponseStrategy from './api-response.strategy'

const SESSION_CACHE_TTL = 5 * 60 * 1000 // 5 минут

let sessionCache: { session: any; timestamp: number } | null = null

async function getCachedSession() {
  const now = Date.now()

  if (sessionCache && now - sessionCache.timestamp < SESSION_CACHE_TTL) {
    return sessionCache.session
  }

  const session = await getSession()
  sessionCache = { session, timestamp: now }

  return session
}

async function json<T>(input: RequestInfo | URL, init?: RequestInit, api_url?: string): Promise<ApiResponse<T>> {
  const session = await getCachedSession()
  let token = session?.user?.token
  let userId = session?.user?.data?.id || 0

  if (!API_URL) throw new Error('Provide API_URL')
  let response: ApiResponse<T> | null = null

  let url = `${API_URL}/${input}`
  url = url.replace(/users\/[^/]+/, `users/${userId}`)

  const requestInit: RequestInit = {
    cache: 'no-cache',
    ...init,
    headers: {
      Authorization: `Bearer ${token}`,
      'Cache-Control': 'no-cache',
      ...(init?.headers || {}),
    },
  }

  try {
    const res = await fetch(api_url || url, requestInit)

    if (!res.ok) {
      let errorMessage = res.statusText

      try {
        const contentType = res.headers.get('content-type')
        if (contentType && contentType.includes('application/json')) {
          const errorResponse = await res.json()
          errorMessage = errorResponse.message || res.statusText
        } else {
          errorMessage = `Ошибка сервера: ${res.status} ${res.statusText}`
        }
      } catch (parseError) {
        errorMessage = `Ошибка сервера: ${res.status} ${res.statusText}`
      }

      if (res.status === 401) {
        clearSessionCache()
      }

      throw new Error(errorMessage)
    }

    const contentType = res.headers.get('content-type')
    if (!contentType || !contentType.includes('application/json')) {
      throw new Error('Сервер вернул некорректный формат данных')
    }

    const json = await res.json()
    response = await apiResponseStrategy({ response: res, json })
  } catch (error: any) {
    response = apiErrorResponse({
      error: error,
    })
  }
  return response as ApiResponse<T>
}

export function clearSessionCache() {
  sessionCache = null
}

export { getCachedSession }

export async function preloadSession() {
  try {
    await getCachedSession()
  } catch (error) {
    console.error('Failed to preload session:', error)
  }
}

export const api = { json }
